import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useApi } from "@/api";
import { DemoUnavailableNote, EmptyRow, Section } from "@/components/manage/Section";
import { PageHeader } from "@/components/manage/PageHeader";
import { StatStrip } from "@/components/manage/StatStrip";
import { StatusBadge } from "@/components/manage/StatusBadge";
import { useIsDemoManage } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/llm-logs")({
  component: LlmLogsPage,
});

type Source = "cd" | "mojo";

interface UsageRow {
  id: string;
  created_at: string | null;
  source: string;
  call_kind: string | null;
  purpose: string | null;
  model: string;
  provider: string | null;
  workspace_id: string | null;
  session_id: string | null;
  agent_id: string | null;
  job_id: string | null;
  // grouping dimensions surfaced as a "Context" column
  workflow_id: string | null;
  workflow_run_id: string | null;
  thread_id: string | null;
  correlation_id: string | null;
  dataset_id: string | null;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  reasoning_tokens: number;
  cost_usd: number | null;
  cost_source: string | null;
  latency_ms: number | null;
  status: string;
  error_message: string | null;
  cached: boolean;
}

interface GroupRow {
  key?: string;
  model?: string;
  call_kind?: string;
  calls: number;
  total_tokens: number;
  cost_usd: number;
}

interface SummaryResponse {
  source: string;
  since_hours: number;
  totals: {
    calls: number;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    cost_usd: number;
    failures: number;
  };
  by_model: GroupRow[];
  by_call_kind: GroupRow[];
}

interface ListResponse {
  source: string;
  rows: UsageRow[];
  count: number;
  total?: number;
  limit?: number;
  offset?: number;
}

const PAGE_SIZE = 50;

interface AggGroup {
  key: string | null;
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: number;
  latency_ms: number;
  failures: number;
  first_at: string | null;
  last_at: string | null;
  workflow_id?: string | null;
  purpose?: string | null;
  thread_id?: string | null;
  dataset_id?: string | null;
  workspace_id?: string | null;
}

interface AggResponse {
  source: string;
  group_by: string;
  groups: AggGroup[];
}

// Group-by options per source. The `dim` doubles as the list-filter query key
// used to drill into a group's individual calls.
const CD_GROUPS = [
  { value: "none", label: "No grouping" },
  { value: "workflow_run_id", label: "Workflow run" },
  { value: "thread_id", label: "Conversation" },
  { value: "correlation_id", label: "Chat response" },
  { value: "purpose", label: "Purpose" },
  { value: "model", label: "Model" },
];
const MOJO_GROUPS = [
  { value: "none", label: "No grouping" },
  { value: "job_id", label: "Ingestion run" },
  { value: "dataset_id", label: "Dataset" },
  { value: "model", label: "Model" },
];

function shortId(s: string | null | undefined): string {
  if (!s) return "—";
  return s.length > 12 ? `${s.slice(0, 10)}…` : s;
}

/** Span between first + last call in a group, human-readable. */
function fmtSpan(first: string | null, last: string | null): string {
  if (!first || !last) return "";
  const ms = new Date(last).getTime() - new Date(first).getTime();
  if (ms <= 0) return "single call";
  return `over ${fmtLatency(ms)}`;
}

function groupLabel(g: AggGroup, dim: string): { label: string; sublabel?: string } {
  switch (dim) {
    case "workflow_run_id":
      return {
        label: g.workflow_id || "Workflow run",
        sublabel: `run ${shortId(g.key)}${g.thread_id ? ` · conversation ${shortId(g.thread_id)}` : ""}`,
      };
    case "thread_id":
      return { label: `Conversation ${shortId(g.key)}`, sublabel: g.purpose || undefined };
    case "correlation_id":
      return {
        label: g.workflow_id ? `Run ${shortId(g.key)}` : `Chat response ${shortId(g.key)}`,
        sublabel: g.purpose || (g.thread_id ? `conversation ${shortId(g.thread_id)}` : undefined),
      };
    case "job_id":
      return {
        label: `Ingestion run ${shortId(g.key)}`,
        sublabel: g.dataset_id ? `dataset ${shortId(g.dataset_id)}` : undefined,
      };
    case "dataset_id":
      return { label: `Dataset ${shortId(g.key)}` };
    case "purpose":
      return { label: g.key || "(no purpose)" };
    default:
      return { label: g.key || "unknown" };
  }
}

/** The grouping-dimension ids to surface per row in the "Context" column —
 * the most-specific run/conversation handle for the source, plus workspace. */
function rowContext(r: UsageRow): Array<{ k: string; v: string }> {
  const out: Array<{ k: string; v: string }> = [];
  if (r.workspace_id) out.push({ k: "ws", v: r.workspace_id });
  if (r.source === "mojo") {
    if (r.job_id) out.push({ k: "job", v: r.job_id });
    else if (r.dataset_id) out.push({ k: "ds", v: r.dataset_id });
  } else {
    if (r.workflow_run_id) out.push({ k: "run", v: r.workflow_run_id });
    else if (r.correlation_id) out.push({ k: "resp", v: r.correlation_id });
    if (r.thread_id) out.push({ k: "conv", v: r.thread_id });
  }
  return out.slice(0, 3);
}

const WINDOWS: { value: string; label: string }[] = [
  { value: "1", label: "Last hour" },
  { value: "24", label: "Last 24h" },
  { value: "168", label: "Last 7 days" },
  { value: "720", label: "Last 30 days" },
];

function fmtCost(n: number | null | undefined): string {
  if (!n) return "$0";
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(2)}`;
}

function fmtNum(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString();
}

/** Compact token counts: 1,933 → "1.9k", 48,533 → "49k", 1,200,000 → "1.2M". */
function fmtCompact(n: number | null | undefined): string {
  const v = n ?? 0;
  if (v < 1000) return v.toLocaleString();
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}k`;
  return `${(v / 1_000_000).toFixed(v < 10_000_000 ? 1 : 0)}M`;
}

/** Human latency: 850 → "850ms", 16,232 → "16.2s", 174,484 → "2m 54s". */
function fmtLatency(ms: number | null | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function LlmLogsPage() {
  // LLM logs are host-ops telemetry — demo-403'd server-side; show a plain
  // note instead of a wall of failed queries if a demo lands here by URL.
  if (useIsDemoManage()) return <DemoUnavailableNote />;
  return <LlmLogsPageInner />;
}

function LlmLogsPageInner() {
  const api = useApi();
  const [source, setSource] = useState<Source>("cd");
  const [since, setSince] = useState("24");
  const [offset, setOffset] = useState(0);
  const [groupBy, setGroupBy] = useState("none");

  // Reset to the first page whenever the source or window changes.
  useEffect(() => {
    setOffset(0);
  }, [source, since]);
  // Group dimensions differ per source, so reset grouping when source flips.
  useEffect(() => {
    setGroupBy("none");
  }, [source]);

  const groupOpts = source === "mojo" ? MOJO_GROUPS : CD_GROUPS;
  const grouped = groupBy !== "none";

  const agg = useQuery({
    queryKey: ["admin-llm-agg", source, since, groupBy],
    queryFn: () =>
      api.request<AggResponse>(
        `/admin/llm-usage/aggregate?source=${source}&group_by=${groupBy}&since_hours=${since}&limit=100`,
      ),
    enabled: grouped,
    refetchInterval: 15000,
  });

  const summary = useQuery({
    queryKey: ["admin-llm-usage-summary", source, since],
    queryFn: () =>
      api.request<SummaryResponse>(
        `/admin/llm-usage/summary?source=${source}&since_hours=${since}`,
      ),
    refetchInterval: 15000,
  });

  const list = useQuery({
    queryKey: ["admin-llm-usage", source, since, offset],
    queryFn: () =>
      api.request<ListResponse>(
        `/admin/llm-usage?source=${source}&since_hours=${since}&limit=${PAGE_SIZE}&offset=${offset}`,
      ),
    refetchInterval: 15000,
  });

  const t = summary.data?.totals;
  const rows = list.data?.rows ?? [];
  const total = list.data?.total; // present for source=cd; mojo may omit
  const pageStart = rows.length ? offset + 1 : 0;
  const pageEnd = offset + rows.length;
  const hasMore = total != null ? pageEnd < total : rows.length === PAGE_SIZE;

  return (
    <div className="mx-auto max-w-[1200px] px-8 py-10">
      <PageHeader
        eyebrow="Admin · Observability"
        title="LLM logs"
        description="Per-call token, cost & latency accounting across the client and data-plane stacks."
        actions={
          <div className="flex items-center gap-2">
            <Select value={source} onValueChange={(v) => setSource(v as Source)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cd">chai-dojo (agent)</SelectItem>
                <SelectItem value="mojo">mojo (data plane)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={since} onValueChange={(v) => setSince(v ?? "")}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WINDOWS.map((w) => (
                  <SelectItem key={w.value} value={w.value}>
                    {w.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={groupBy} onValueChange={(v) => setGroupBy(v ?? "")}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Group by" />
              </SelectTrigger>
              <SelectContent>
                {groupOpts.map((g) => (
                  <SelectItem key={g.value} value={g.value}>
                    {g.value === "none" ? g.label : `Group: ${g.label}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {summary.isLoading ? (
        <Skeleton className="mb-10 h-24 w-full" />
      ) : (
        <StatStrip
          items={[
            { label: "Calls", value: fmtNum(t?.calls), hint: `${fmtNum(t?.failures)} failed` },
            { label: "Total tokens", value: fmtNum(t?.total_tokens), hint: `${fmtNum(t?.prompt_tokens)} in / ${fmtNum(t?.completion_tokens)} out` },
            { label: "Cost", value: fmtCost(t?.cost_usd), hint: source === "cd" ? "OpenRouter (header)" : "OpenRouter (inline)" },
            { label: "Source", value: source === "cd" ? "chai-dojo" : "mojo", hint: WINDOWS.find((w) => w.value === since)?.label },
          ]}
        />
      )}

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="By model">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-hairline)] text-left text-xs text-muted-foreground">
                <th className="py-2 font-medium">Model</th>
                <th className="py-2 text-right font-medium">Calls</th>
                <th className="py-2 text-right font-medium">Tokens</th>
                <th className="py-2 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {(summary.data?.by_model ?? []).map((m, i) => (
                <tr key={i} className="border-b border-[var(--color-hairline)]/50">
                  <td className="py-1.5 font-mono text-xs">{m.key ?? m.model}</td>
                  <td className="py-1.5 text-right">{fmtNum(m.calls)}</td>
                  <td className="py-1.5 text-right">{fmtNum(m.total_tokens)}</td>
                  <td className="py-1.5 text-right">{fmtCost(m.cost_usd)}</td>
                </tr>
              ))}
              {(summary.data?.by_model ?? []).length === 0 && (
                <EmptyRow colSpan={4}>No usage in this window.</EmptyRow>
              )}
            </tbody>
          </table>
        </Section>

        <Section title="By call kind">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-hairline)] text-left text-xs text-muted-foreground">
                <th className="py-2 font-medium">Kind</th>
                <th className="py-2 text-right font-medium">Calls</th>
                <th className="py-2 text-right font-medium">Tokens</th>
                <th className="py-2 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {(summary.data?.by_call_kind ?? []).map((k, i) => (
                <tr key={i} className="border-b border-[var(--color-hairline)]/50">
                  <td className="py-1.5">{k.key ?? k.call_kind}</td>
                  <td className="py-1.5 text-right">{fmtNum(k.calls)}</td>
                  <td className="py-1.5 text-right">{fmtNum(k.total_tokens)}</td>
                  <td className="py-1.5 text-right">{fmtCost(k.cost_usd)}</td>
                </tr>
              ))}
              {(summary.data?.by_call_kind ?? []).length === 0 && (
                <EmptyRow colSpan={4}>No usage in this window.</EmptyRow>
              )}
            </tbody>
          </table>
        </Section>
      </div>

      {grouped ? (
        <GroupedCalls
          loading={agg.isLoading}
          groups={agg.data?.groups ?? []}
          groupBy={groupBy}
          groupLabelText={groupOpts.find((g) => g.value === groupBy)?.label ?? groupBy}
          source={source}
          since={since}
        />
      ) : (
      <Section
        title={`Recent calls${total != null ? ` (${fmtNum(total)})` : rows.length ? ` (${rows.length})` : ""}`}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-hairline)] text-left text-xs text-muted-foreground">
                <th className="py-2 font-medium">Time</th>
                <th className="py-2 font-medium">Model</th>
                <th className="py-2 font-medium">Kind</th>
                <th className="py-2 font-medium">Purpose</th>
                <th className="py-2 font-medium">Context</th>
                <th className="py-2 text-right font-medium">Tokens (in/out)</th>
                <th className="py-2 text-right font-medium">Cost</th>
                <th className="py-2 text-right font-medium">Latency</th>
                <th className="py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr>
                  <td colSpan={9} className="py-4">
                    <Skeleton className="h-5 w-full" />
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-[var(--color-hairline)]/50 align-top"
                  title={
                    [
                      r.workspace_id && `workspace: ${r.workspace_id}`,
                      r.session_id && `session: ${r.session_id}`,
                      r.agent_id && `agent: ${r.agent_id}`,
                      r.job_id && `job: ${r.job_id}`,
                      r.cost_source && `cost: ${r.cost_source}`,
                      r.error_message && `error: ${r.error_message}`,
                    ]
                      .filter(Boolean)
                      .join("\n") || undefined
                  }
                >
                  <td className="whitespace-nowrap py-1.5 text-xs text-muted-foreground">{fmtTime(r.created_at)}</td>
                  <td className="py-1.5 font-mono text-xs">{r.model}</td>
                  <td className="py-1.5 text-xs">{r.call_kind ?? "—"}</td>
                  <td className="py-1.5 text-xs">{r.purpose ?? "—"}</td>
                  <td className="py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {rowContext(r).map((c) => (
                        <span
                          key={c.k}
                          title={c.v}
                          className="inline-flex items-center gap-0.5 rounded-sm bg-muted px-1 py-0 font-mono text-[10px] text-muted-foreground"
                        >
                          <span className="text-foreground/60">{c.k}</span>…{c.v.slice(-6)}
                        </span>
                      ))}
                      {rowContext(r).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </td>
                  <td
                    className="whitespace-nowrap py-1.5 text-right tabular-nums"
                    title={`${fmtNum(r.prompt_tokens)} in / ${fmtNum(r.completion_tokens)} out${r.reasoning_tokens ? ` / ${fmtNum(r.reasoning_tokens)} reasoning` : ""}`}
                  >
                    {fmtCompact(r.prompt_tokens)} / {fmtCompact(r.completion_tokens)}
                    {r.reasoning_tokens ? (
                      <span className="text-muted-foreground"> +{fmtCompact(r.reasoning_tokens)}r</span>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap py-1.5 text-right tabular-nums">{fmtCost(r.cost_usd)}</td>
                  <td
                    className="whitespace-nowrap py-1.5 text-right tabular-nums"
                    title={r.latency_ms != null ? `${fmtNum(r.latency_ms)} ms` : undefined}
                  >
                    {fmtLatency(r.latency_ms)}
                  </td>
                  <td className="py-1.5">
                    <StatusBadge label={r.cached ? "cached" : r.status} status={r.status} />
                  </td>
                </tr>
              ))}
              {!list.isLoading && rows.length === 0 && (
                <EmptyRow colSpan={9}>No LLM calls recorded in this window.</EmptyRow>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {total != null
              ? `${fmtNum(pageStart)}–${fmtNum(pageEnd)} of ${fmtNum(total)}`
              : rows.length
                ? `Showing ${fmtNum(rows.length)}`
                : ""}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || list.isFetching}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasMore || list.isFetching}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      </Section>
      )}
    </div>
  );
}

// ── Grouped (hierarchy) view ──────────────────────────────────────────────────

function GroupedCalls({
  loading,
  groups,
  groupBy,
  groupLabelText,
  source,
  since,
}: {
  loading: boolean;
  groups: AggGroup[];
  groupBy: string;
  groupLabelText: string;
  source: Source;
  since: string;
}) {
  return (
    <Section title={`By ${groupLabelText.toLowerCase()}${groups.length ? ` (${groups.length})` : ""}`}>
      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : groups.length === 0 ? (
        <p className="px-1 py-8 text-center text-xs text-muted-foreground">
          No grouped usage in this window.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {groups.map((g) => (
            <GroupRow
              key={g.key ?? "—"}
              group={g}
              dim={groupBy}
              source={source}
              since={since}
            />
          ))}
        </ul>
      )}
    </Section>
  );
}

function GroupRow({
  group,
  dim,
  source,
  since,
}: {
  group: AggGroup;
  dim: string;
  source: Source;
  since: string;
}) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const { label, sublabel } = groupLabel(group, dim);

  // Lazy: fetch this group's individual calls only once expanded.
  const calls = useQuery({
    queryKey: ["admin-llm-group", source, since, dim, group.key],
    queryFn: () =>
      api.request<ListResponse>(
        `/admin/llm-usage?source=${source}&since_hours=${since}&limit=200&${dim}=${encodeURIComponent(group.key ?? "")}`,
      ),
    enabled: open && !!group.key,
  });

  return (
    <li className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-[var(--color-bg-elevated)]">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-foreground/[0.025]">
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-90",
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-foreground">{label}</div>
            {sublabel && (
              <div className="truncate font-mono text-[10.5px] text-muted-foreground">{sublabel}</div>
            )}
          </div>
          <div className="hidden shrink-0 items-baseline gap-3 text-xs tabular-nums text-muted-foreground sm:flex">
            <span>
              <span className="font-medium text-foreground">{fmtNum(group.calls)}</span> calls
            </span>
            <span>
              <span className="font-medium text-foreground">{fmtCompact(group.total_tokens)}</span> tok
            </span>
            <span className="font-medium text-foreground">{fmtCost(group.cost_usd)}</span>
            <span title="total latency across the group">{fmtLatency(group.latency_ms)}</span>
            {group.failures > 0 && (
              <span className="text-destructive">{fmtNum(group.failures)} failed</span>
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-[var(--color-hairline)] bg-background/40 px-4 py-3">
            <div className="mb-2 text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
              {dim === "workflow_run_id" ? "Calls the agent made" : "Calls"} {fmtSpan(group.first_at, group.last_at)}
            </div>
            {calls.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <CallTimeline rows={calls.data?.rows ?? []} />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}

/** The vertical call sequence — the "diagram of calls the agent did" for a run. */
function CallTimeline({ rows }: { rows: UsageRow[] }) {
  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">No calls in window.</p>;
  }
  // List endpoint returns newest-first; show oldest-first so the sequence reads top→bottom.
  const ordered = [...rows].reverse();
  return (
    <ol className="relative ml-1 border-l border-[var(--color-hairline)]">
      {ordered.map((r, i) => (
        <li key={r.id} className="relative mb-3 pl-5 last:mb-0">
          <span className="absolute top-1 -left-[5px] size-2.5 rounded-full border-2 border-[var(--color-bg-elevated)] bg-primary" />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-[11px] text-muted-foreground">{i + 1}.</span>
            <span className="font-mono text-xs text-foreground">{r.model}</span>
            {r.agent_id && r.agent_id !== "orchestrator" && (
              <span className="rounded-sm bg-muted px-1 text-[10px] text-muted-foreground">{r.agent_id}</span>
            )}
            {r.call_kind && r.call_kind !== "orchestrator" && (
              <span className="rounded-sm bg-muted px-1 text-[10px] text-muted-foreground">{r.call_kind}</span>
            )}
            <StatusBadge label={r.cached ? "cached" : r.status} status={r.status} />
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums text-muted-foreground">
            <span title={`${fmtNum(r.prompt_tokens)} in / ${fmtNum(r.completion_tokens)} out`}>
              {fmtCompact(r.prompt_tokens)} / {fmtCompact(r.completion_tokens)} tok
            </span>
            <span>{fmtCost(r.cost_usd)}</span>
            <span title={r.latency_ms != null ? `${fmtNum(r.latency_ms)} ms` : undefined}>
              {fmtLatency(r.latency_ms)}
            </span>
            <span>{fmtTime(r.created_at)}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}
